
const libPictView = require('pict-view');

/**
 * ErrorLogViewer — read-only diagnostics page for recent Headlight web errors (HLWebErrorLog artifacts).
 * A per-day bar chart + a filterable, expandable table. All data + filtering live in the ErrorLogViewer
 * provider; the view paints declaratively. The free-text filter and row expand/collapse repaint ONLY the
 * table host (via syncTable) so the search input keeps focus and the chart isn't rebuilt.
 */
class ErrorLogViewer extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get provider()
	{
		return this.pict.providers.ErrorLogViewer;
	}

	onBeforeRender(pRenderable)
	{
		if (this.provider) { this.provider.deriveRenderState(); }
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	/** Repaint only the table host (count label + table) — keeps the search input focused on each keystroke. */
	syncTable()
	{
		if (this.pict.ContentAssignment.getElement('#ELV-TableHost').length === 0) { return; }
		const tmpHTML = this.pict.parseTemplateByHash('ErrorLogViewer-Table', this.pict.AppData.ErrorLogViewer);
		this.pict.ContentAssignment.assignContent('#ELV-TableHost', tmpHTML);
	}
}

module.exports = ErrorLogViewer;

module.exports.default_configuration =
{
	ViewIdentifier: 'ErrorLogViewer',
	DefaultRenderable: 'ErrorLogViewerWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,
	CSS: /*css*/`
		.elv-wrap { padding: 1.25rem 1.5rem 2rem; }
		.elv-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; margin: 0 0 1rem; flex-wrap: wrap; }
		.elv-eyebrow { font-size: 0.72rem; font-weight: 650; text-transform: uppercase; letter-spacing: 0.06em; color: var(--theme-color-text-muted, #6b7686); }
		.elv-title { font-size: 1.5rem; font-weight: 700; line-height: 1.2; margin: 0.1rem 0 0; color: var(--theme-color-text-primary, #1f2733); }
		.elv-sub { font-size: 0.9rem; color: var(--theme-color-text-muted, #6b7686); margin-top: 0.15rem; }
		.elv-panel { border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 10px; background: var(--theme-color-background-panel, #fff); padding: 1.25rem 1.4rem; margin-bottom: 1.1rem; }
		.elv-summary { display: flex; gap: 0.6rem; flex-wrap: wrap; align-items: center; margin-bottom: 0.9rem; }
		.elv-stat { font-size: 1.65rem; font-weight: 750; color: var(--theme-color-text-primary, #1f2733); line-height: 1; }
		.elv-stat-label { font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-muted, #6b7686); }
		.elv-chip { display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.8rem; font-weight: 600; padding: 0.2rem 0.6rem; border-radius: 999px; background: var(--theme-color-background-tertiary, #f4f6f9); color: var(--theme-color-text-secondary, #45505f); }
		.elv-chip .elv-dot { width: 0.55rem; height: 0.55rem; border-radius: 50%; background: var(--theme-color-text-muted, #9aa4b2); }
		.elv-app-fieldbook .elv-dot, .elv-badge.elv-app-fieldbook { background: var(--theme-color-brand-primary, #5650e6); }
		.elv-app-portal .elv-dot, .elv-badge.elv-app-portal { background: var(--theme-color-status-info, #3083dc); }
		.elv-app-tracker .elv-dot, .elv-badge.elv-app-tracker { background: var(--theme-color-status-success, #2aa65a); }
		.elv-app-unknown .elv-dot, .elv-badge.elv-app-unknown { background: var(--theme-color-text-muted, #9aa4b2); }
		/* Per-day bar chart (dependency-free). */
		.elv-chart-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 0.4rem; }
		.elv-chart-title { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-muted, #6b7686); }
		.elv-chart-peak { font-size: 0.75rem; color: var(--theme-color-text-muted, #6b7686); }
		.elv-chart { display: flex; align-items: flex-end; gap: 2px; height: 130px; padding-top: 0.4rem; border-bottom: 1px solid var(--theme-color-border-default, #d7dce3); }
		.elv-bar-col { flex: 1 1 0; min-width: 3px; height: 100%; display: flex; align-items: flex-end; cursor: default; }
		.elv-bar { width: 100%; background: var(--theme-color-brand-primary, #5650e6); border-radius: 2px 2px 0 0; min-height: 0; opacity: 0.85; transition: opacity 0.15s ease; }
		.elv-bar-col:hover .elv-bar { opacity: 1; }
		.elv-bar.is-max { background: var(--theme-color-status-warning, #f5a623); opacity: 1; }
		.elv-axis { display: flex; gap: 2px; margin-top: 0.3rem; }
		.elv-axis-col { flex: 1 1 0; min-width: 3px; font-size: 0.62rem; color: var(--theme-color-text-muted, #9aa4b2); text-align: center; white-space: nowrap; }
		/* Controls. */
		.elv-controls { display: flex; justify-content: space-between; gap: 1rem; align-items: center; flex-wrap: wrap; margin-bottom: 0.8rem; }
		.elv-filters { display: flex; gap: 0.35rem; flex-wrap: wrap; }
		.elv-filter { border: 1px solid var(--theme-color-border-default, #d7dce3); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #45505f);
			font-size: 0.82rem; font-weight: 600; padding: 0.3rem 0.7rem; border-radius: 7px; cursor: pointer; display: inline-flex; align-items: center; gap: 0.35rem; }
		.elv-filter:hover { background: var(--theme-color-background-tertiary, #f4f6f9); }
		.elv-filter.is-active { background: var(--theme-color-brand-primary, #5650e6); border-color: var(--theme-color-brand-primary, #5650e6); color: #fff; }
		.elv-filter-count { font-size: 0.7rem; opacity: 0.8; background: rgba(0,0,0,0.08); border-radius: 999px; padding: 0 0.4rem; }
		.elv-filter.is-active .elv-filter-count { background: rgba(255,255,255,0.25); }
		.elv-search { max-width: 22rem; }
		.elv-count-label { font-size: 0.8rem; color: var(--theme-color-text-muted, #6b7686); margin: 0 0 0.5rem; }
		/* Table. */
		.elv-table { width: 100%; border-collapse: separate; border-spacing: 0; border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 8px; overflow: hidden; }
		.elv-table th { text-align: left; font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-muted, #6b7686);
			background: var(--theme-color-background-tertiary, #f4f6f9); padding: 0.45rem 0.8rem; border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); }
		.elv-row > td { padding: 0.5rem 0.8rem; border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); font-size: 0.88rem; color: var(--theme-color-text-primary, #1f2733); vertical-align: top; }
		.elv-row { cursor: pointer; }
		.elv-row:hover > td { background: var(--theme-color-background-selected, #eef1f8); }
		.elv-row.is-expanded > td { background: var(--theme-color-background-selected, #eef1f8); border-bottom: none; }
		.elv-time { white-space: nowrap; color: var(--theme-color-text-secondary, #45505f); font-size: 0.82rem; }
		.elv-badge { display: inline-block; color: #fff; font-size: 0.7rem; font-weight: 700; padding: 0.1rem 0.5rem; border-radius: 5px; white-space: nowrap; }
		.elv-msg { font-weight: 500; word-break: break-word; }
		.elv-context { white-space: nowrap; color: var(--theme-color-text-muted, #6b7686); font-size: 0.78rem; }
		.elv-chev { color: var(--theme-color-text-muted, #9aa4b2); text-align: right; }
		.elv-detail-row > td { padding: 0 0.8rem 0.8rem; background: var(--theme-color-background-selected, #eef1f8); border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); }
		.elv-detail-head { font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-muted, #6b7686); margin: 0.6rem 0 0.25rem; }
		.elv-pre { margin: 0; padding: 0.7rem 0.85rem; border-radius: 6px; background: var(--theme-color-background-panel, #fff); border: 1px solid var(--theme-color-border-light, #e8ebf0);
			font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.76rem; line-height: 1.5; color: var(--theme-color-text-secondary, #45505f); overflow: auto; max-height: 18rem; white-space: pre-wrap; word-break: break-word; }
		.elv-empty { padding: 2.5rem 1rem; }
		.elv-spinner { display: inline-block; width: 1.6rem; height: 1.6rem; border: 3px solid var(--theme-color-border-default, #dbdbdb); border-top-color: var(--theme-color-brand-primary, #5650e6); border-radius: 50%; animation: elv-spin 0.8s linear infinite; }
		@keyframes elv-spin { to { transform: rotate(360deg); } }
	`,
	Templates:
	[
		{
			Hash: 'ErrorLogViewer-Wrap',
			Template: /*html*/`
				<div class="elv-wrap">
					<div class="elv-head">
						<div>
							<div class="elv-eyebrow">Diagnostics</div>
							<h1 class="elv-title">Web Error Log</h1>
							<div class="elv-sub">Client-side errors reported by the Headlight web apps · {~D:AppData.ErrorLogViewer.WindowLabel~}</div>
						</div>
						<button class="button is-light" onclick="_Pict.providers.ErrorLogViewer.reload()"><span class="icon"><i class="fas fa-rotate-right"></i></span><span>Refresh</span></button>
					</div>
					{~TS:ErrorLogViewer-Message:AppData.ErrorLogViewer.MessageSlot~}
					{~TS:ErrorLogViewer-Loading:AppData.ErrorLogViewer.LoadingSlot~}
					{~TS:ErrorLogViewer-Body:AppData.ErrorLogViewer.BodySlot~}
				</div>`
		},
		{
			Hash: 'ErrorLogViewer-Body',
			Template: /*html*/`
				<div class="elv-panel">
					<div class="elv-summary">
						<div>
							<div class="elv-stat">{~D:AppData.ErrorLogViewer.Total~}</div>
							<div class="elv-stat-label">errors in window</div>
						</div>
						<div style="flex:1"></div>
						{~TS:ErrorLogViewer-AppChip:AppData.ErrorLogViewer.AppChips~}
					</div>
					<div class="elv-chart-head">
						<span class="elv-chart-title">Errors per day</span>
						<span class="elv-chart-peak">{~D:AppData.ErrorLogViewer.ChartMaxLabel~}</span>
					</div>
					<div class="elv-chart">{~TS:ErrorLogViewer-Bar:AppData.ErrorLogViewer.Bars~}</div>
					<div class="elv-axis">{~TS:ErrorLogViewer-Axis:AppData.ErrorLogViewer.Bars~}</div>
				</div>
				<div class="elv-controls">
					<div class="elv-filters">{~TS:ErrorLogViewer-Filter:AppData.ErrorLogViewer.FilterButtons~}</div>
					<input class="input elv-search" type="text" placeholder="Filter by message, app, or stack…" value="{~D:AppData.ErrorLogViewer.TextFilter~}" oninput="_Pict.providers.ErrorLogViewer.setTextFilter(this.value)" />
				</div>
				<div id="ELV-TableHost">{~T:ErrorLogViewer-Table~}</div>`
		},
		{
			Hash: 'ErrorLogViewer-AppChip',
			Template: /*html*/`<span class="elv-chip {~D:Record.AppClass~}"><span class="elv-dot"></span>{~D:Record.Label~} · {~D:Record.Count~}</span>`
		},
		{
			Hash: 'ErrorLogViewer-Bar',
			Template: /*html*/`<div class="elv-bar-col" title="{~D:Record.Tooltip~}"><div class="elv-bar {~D:Record.MaxClass~}" style="height: {~D:Record.HeightPct~}%"></div></div>`
		},
		{
			Hash: 'ErrorLogViewer-Axis',
			Template: /*html*/`<div class="elv-axis-col">{~D:Record.AxisLabel~}</div>`
		},
		{
			Hash: 'ErrorLogViewer-Filter',
			Template: /*html*/`<button class="elv-filter {~D:Record.ActiveClass~}" onclick="_Pict.providers.ErrorLogViewer.setAppFilter('{~D:Record.Key~}')">{~D:Record.Label~} <span class="elv-filter-count">{~D:Record.Count~}</span></button>`
		},
		{
			Hash: 'ErrorLogViewer-Table',
			Template: /*html*/`
				<p class="elv-count-label">{~D:AppData.ErrorLogViewer.TableCountLabel~}</p>
				<table class="elv-table">
					<thead><tr><th>Time</th><th>App</th><th>Message</th><th>Context</th><th></th></tr></thead>
					<tbody>{~TS:ErrorLogViewer-Row:AppData.ErrorLogViewer.Rows~}</tbody>
				</table>
				{~TS:ErrorLogViewer-TableEmpty:AppData.ErrorLogViewer.TableEmptySlot~}`
		},
		{
			Hash: 'ErrorLogViewer-Row',
			Template: /*html*/`
				<tr class="elv-row {~D:Record.RowClass~}" onclick="_Pict.providers.ErrorLogViewer.toggleRow({~D:Record.ID~})">
					<td class="elv-time">{~D:Record.Time~}</td>
					<td><span class="elv-badge {~D:Record.AppClass~}">{~D:Record.AppDisplay~}</span></td>
					<td class="elv-msg">{~D:Record.Message~}</td>
					<td class="elv-context">{~D:Record.Context~}</td>
					<td class="elv-chev"><i class="fas {~D:Record.Chevron~}"></i></td>
				</tr>
				{~TS:ErrorLogViewer-RowDetail:Record.DetailSlot~}`
		},
		{
			Hash: 'ErrorLogViewer-RowDetail',
			Template: /*html*/`
				<tr class="elv-detail-row">
					<td colspan="5">
						<div class="elv-detail-head">Stack trace</div>
						<pre class="elv-pre">{~D:Record.Stack~}</pre>
						{~TS:ErrorLogViewer-Cause:Record.CauseSlot~}
						<div class="elv-detail-head">Full payload</div>
						<pre class="elv-pre">{~D:Record.JSON~}</pre>
					</td>
				</tr>`
		},
		{
			Hash: 'ErrorLogViewer-Cause',
			Template: /*html*/`<div class="elv-detail-head">Context</div><pre class="elv-pre">{~D:Record.Cause~}</pre>`
		},
		{
			Hash: 'ErrorLogViewer-TableEmpty',
			Template: /*html*/`<p class="has-text-grey has-text-centered py-5">No errors match the current filters.</p>`
		},
		{
			Hash: 'ErrorLogViewer-Message',
			Template: /*html*/`<div class="notification {~D:Record.MessageClass~} is-soft py-2 px-3 mb-3">{~D:Record.Message~}</div>`
		},
		{
			Hash: 'ErrorLogViewer-Loading',
			Template: /*html*/`<div class="elv-panel elv-empty has-text-grey has-text-centered"><span class="elv-spinner"></span><p class="mt-3">Loading the error log…</p></div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'ErrorLogViewerWrap',
			TemplateHash: 'ErrorLogViewer-Wrap',
			ContentDestinationAddress: '#PlatformConfiguration-Application-Container'
		}
	]
};
